/* Gameday school and Ottertex color-match component v2. */
(() => {
  const MATCH_SCALE = 2;
  const FALLBACK_COLORS = {
    a: "#007941",
    b: "#FCDF2B",
  };

  const clamp = (value, minimum, maximum) =>
    Math.min(maximum, Math.max(minimum, value));

  const degrees = (radians) => (radians * 180) / Math.PI;
  const radians = (degreesValue) => (degreesValue * Math.PI) / 180;

  function normalizeHue(hue) {
    return hue < 0 ? hue + 360 : hue;
  }

  function hexToLab(hex) {
    const normalized = String(hex || "")
      .replace("#", "")
      .trim();
    if (!/^[0-9a-f]{6}$/i.test(normalized)) return [0, 0, 0];

    const channels = [0, 2, 4].map((offset) => {
      const encoded = Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255;
      return encoded <= 0.04045
        ? encoded / 12.92
        : ((encoded + 0.055) / 1.055) ** 2.4;
    });

    const [red, green, blue] = channels;
    const x = (red * 0.4124564 + green * 0.3575761 + blue * 0.1804375) / 0.95047;
    const y = red * 0.2126729 + green * 0.7151522 + blue * 0.072175;
    const z = (red * 0.0193339 + green * 0.119192 + blue * 0.9503041) / 1.08883;
    const delta = 6 / 29;
    const convert = (value) =>
      value > delta ** 3 ? Math.cbrt(value) : value / (3 * delta ** 2) + 4 / 29;
    const fx = convert(x);
    const fy = convert(y);
    const fz = convert(z);
    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }

  function deltaE2000(first, second) {
    const [l1, a1, b1] = first;
    const [l2, a2, b2] = second;
    const chroma1 = Math.hypot(a1, b1);
    const chroma2 = Math.hypot(a2, b2);
    const meanChroma = (chroma1 + chroma2) / 2;
    const meanChromaPower = meanChroma ** 7;
    const g =
      0.5 *
      (1 -
        Math.sqrt(
          meanChromaPower / (meanChromaPower + 25 ** 7),
        ));
    const adjustedA1 = (1 + g) * a1;
    const adjustedA2 = (1 + g) * a2;
    const adjustedChroma1 = Math.hypot(adjustedA1, b1);
    const adjustedChroma2 = Math.hypot(adjustedA2, b2);
    const hue1 = normalizeHue(degrees(Math.atan2(b1, adjustedA1)));
    const hue2 = normalizeHue(degrees(Math.atan2(b2, adjustedA2)));
    const deltaLightness = l2 - l1;
    const deltaChroma = adjustedChroma2 - adjustedChroma1;

    let deltaHue = 0;
    if (adjustedChroma1 * adjustedChroma2 !== 0) {
      deltaHue = hue2 - hue1;
      if (deltaHue > 180) deltaHue -= 360;
      if (deltaHue < -180) deltaHue += 360;
    }
    const deltaHueTerm =
      2 *
      Math.sqrt(adjustedChroma1 * adjustedChroma2) *
      Math.sin(radians(deltaHue / 2));
    const meanLightness = (l1 + l2) / 2;
    const meanAdjustedChroma = (adjustedChroma1 + adjustedChroma2) / 2;

    let meanHue = hue1 + hue2;
    if (adjustedChroma1 * adjustedChroma2 === 0) {
      meanHue = hue1 + hue2;
    } else if (Math.abs(hue1 - hue2) <= 180) {
      meanHue = (hue1 + hue2) / 2;
    } else if (hue1 + hue2 < 360) {
      meanHue = (hue1 + hue2 + 360) / 2;
    } else {
      meanHue = (hue1 + hue2 - 360) / 2;
    }

    const t =
      1 -
      0.17 * Math.cos(radians(meanHue - 30)) +
      0.24 * Math.cos(radians(2 * meanHue)) +
      0.32 * Math.cos(radians(3 * meanHue + 6)) -
      0.2 * Math.cos(radians(4 * meanHue - 63));
    const lightnessScale =
      1 +
      (0.015 * (meanLightness - 50) ** 2) /
        Math.sqrt(20 + (meanLightness - 50) ** 2);
    const chromaScale = 1 + 0.045 * meanAdjustedChroma;
    const hueScale = 1 + 0.015 * meanAdjustedChroma * t;
    const rotationDegrees =
      30 * Math.exp(-(((meanHue - 275) / 25) ** 2));
    const meanAdjustedChromaPower = meanAdjustedChroma ** 7;
    const chromaRotation =
      2 *
      Math.sqrt(
        meanAdjustedChromaPower /
          (meanAdjustedChromaPower + 25 ** 7),
      );
    const rotation =
      -Math.sin(radians(2 * rotationDegrees)) * chromaRotation;
    const lightnessTerm = deltaLightness / lightnessScale;
    const chromaTerm = deltaChroma / chromaScale;
    const hueTerm = deltaHueTerm / hueScale;
    return Math.sqrt(
      lightnessTerm ** 2 +
        chromaTerm ** 2 +
        hueTerm ** 2 +
        rotation * chromaTerm * hueTerm,
    );
  }

  function matchPercent(referenceHex, supplierHex) {
    const difference = deltaE2000(hexToLab(referenceHex), hexToLab(supplierHex));
    return Math.round(clamp(100 - MATCH_SCALE * difference, 0, 100));
  }

  async function initialize(section) {
    if (
      !section ||
      section.dataset.bagMatchReady === "true" ||
      section.dataset.bagProductLoading === "true"
    ) {
      return;
    }
    section.dataset.bagProductLoading = "true";
    let catalogData = window.EXOGamedayBagColorMatchData;
    const embeddedData = document.querySelector("[data-bag-color-data]");
    if (!catalogData?.colors?.length && embeddedData?.textContent) {
      try {
        catalogData = JSON.parse(embeddedData.textContent);
      } catch (_error) {
        catalogData = null;
      }
    }
    if (
      !catalogData?.colors?.length &&
      section.dataset.bagColorDataUrl &&
      typeof window.fetch === "function"
    ) {
      try {
        const response = await window.fetch(section.dataset.bagColorDataUrl, {
          credentials: "same-origin",
        });
        if (response.ok) {
          catalogData = await response.json();
        }
      } catch (_error) {
        catalogData = null;
      }
    }
    section.dataset.bagCatalogCount = String(catalogData?.colors?.length || 0);
    delete section.dataset.bagProductLoading;
    section.dataset.bagMatchReady = "true";
    section.dataset.bagProductReady = "true";

    const motionImage = section.querySelector("[data-bag-motion-image]");
    const motionToggle = section.querySelector("[data-bag-motion-toggle]");
    const schoolSelect = section.querySelector("[data-bag-school-select]");
    const matchPhotoLabel = section.querySelector("[data-bag-match-photo-label]");
    const catalogGrid = section.querySelector("[data-bag-fwd-grid]");
    const catalogSchool = section.querySelector("[data-bag-catalog-school]");
    const previewButtons = Array.from(
      section.querySelectorAll("[data-bag-preview-mode]"),
    );
    const catalogButtons = Array.from(
      section.querySelectorAll("[data-bag-catalog-target]"),
    );
    const colorInputs = {
      a: section.querySelector('[data-bag-color-input="a"]'),
      b: section.querySelector('[data-bag-color-input="b"]'),
    };
    const colorOutputs = {
      a: section.querySelector('[data-bag-color-output="a"]'),
      b: section.querySelector('[data-bag-color-output="b"]'),
    };
    const previewLabels = {
      a: section.querySelector('[data-bag-preview-label="a"]'),
      b: section.querySelector('[data-bag-preview-label="b"]'),
    };
    const propertyInputs = {
      a: section.querySelector("[data-bag-property-a]"),
      b: section.querySelector("[data-bag-property-b]"),
      school: section.querySelector("[data-bag-property-school]"),
      matchA: section.querySelector("[data-bag-property-match-a]"),
      matchB: section.querySelector("[data-bag-property-match-b]"),
    };
    const quoteLink = section.querySelector("[data-bag-quote-link]");
    const state = {
      school: null,
      previewMode: "school",
      catalogTarget: "a",
      matches: { a: [], b: [] },
    };

    const currentManualColors = () => ({
      a: colorInputs.a?.value.toUpperCase() || FALLBACK_COLORS.a,
      b: colorInputs.b?.value.toUpperCase() || FALLBACK_COLORS.b,
    });

    const schoolColors = () => ({
      a: state.school?.colors?.[0] || {
        name: "Green",
        hex: FALLBACK_COLORS.a,
        reference: "",
      },
      b: state.school?.colors?.[1] || {
        name: "Yellow",
        hex: FALLBACK_COLORS.b,
        reference: "",
      },
    });

    const setButtonGroupState = (buttons, value, dataKey) => {
      buttons.forEach((button) => {
        const active = button.dataset[dataKey] === value;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });
    };

    const updateQuote = () => {
      const colors = currentManualColors();
      const bestA = state.matches.a[0];
      const bestB = state.matches.b[0];
      if (propertyInputs.a) propertyInputs.a.value = colors.a;
      if (propertyInputs.b) propertyInputs.b.value = colors.b;
      if (propertyInputs.school) {
        propertyInputs.school.value = state.school?.name || "Custom";
      }
      if (propertyInputs.matchA) {
        propertyInputs.matchA.value = bestA
          ? `${bestA.name} / ${bestA.sku} / ${bestA.score}% screen match`
          : "";
      }
      if (propertyInputs.matchB) {
        propertyInputs.matchB.value = bestB
          ? `${bestB.name} / ${bestB.sku} / ${bestB.score}% screen match`
          : "";
      }

      if (!quoteLink) return;
      const url = new URL(quoteLink.href, window.location.origin);
      url.searchParams.set("school", state.school?.name || "Custom");
      url.searchParams.set("color_a", colors.a);
      url.searchParams.set("color_b", colors.b);
      url.searchParams.set("preview_source", state.previewMode);
      if (bestA) {
        url.searchParams.set("ottertex_a", `${bestA.name} ${bestA.sku}`);
        url.searchParams.set("match_a", `${bestA.score}%`);
      }
      if (bestB) {
        url.searchParams.set("ottertex_b", `${bestB.name} ${bestB.sku}`);
        url.searchParams.set("match_b", `${bestB.score}%`);
      }
      quoteLink.href = url.toString();
    };

    const setPhotoColors = (colors) => {
      section.style.setProperty("--bag-photo-color-a", colors.a);
      section.style.setProperty("--bag-photo-color-b", colors.b);
    };

    const applyPhotoPreview = () => {
      const targets = schoolColors();
      if (state.previewMode === "fwd") {
        setPhotoColors({
          a: state.matches.a[0]?.screenSampleHex || targets.a.hex,
          b: state.matches.b[0]?.screenSampleHex || targets.b.hex,
        });
        if (matchPhotoLabel) {
          matchPhotoLabel.textContent = `CLOSEST OTTERTEX PREVIEW / ${
            state.school?.name || "CUSTOM"
          }`;
        }
      } else if (state.previewMode === "custom") {
        setPhotoColors(currentManualColors());
        if (matchPhotoLabel) {
          matchPhotoLabel.textContent = "CUSTOM COLOR PREVIEW";
        }
      } else {
        setPhotoColors({ a: targets.a.hex, b: targets.b.hex });
        if (matchPhotoLabel) {
          matchPhotoLabel.textContent = `SCHOOL REFERENCE PREVIEW / ${
            state.school?.name || "OREGON"
          }`;
        }
      }
      setButtonGroupState(
        previewButtons,
        state.previewMode,
        "bagPreviewMode",
      );
      updateQuote();
    };

    const updateConfiguration = ({ manual = false } = {}) => {
      const colors = currentManualColors();
      section.style.setProperty("--bag-color-a", colors.a);
      section.style.setProperty("--bag-color-b", colors.b);
      ["a", "b"].forEach((side) => {
        if (colorOutputs[side]) colorOutputs[side].textContent = colors[side];
        if (previewLabels[side]) previewLabels[side].textContent = colors[side];
      });
      if (manual) state.previewMode = "custom";
      applyPhotoPreview();
    };

    const setMotionState = (playing) => {
      if (!motionImage || !motionToggle) return;
      motionImage.src = playing
        ? motionImage.dataset.motionSrc
        : motionImage.dataset.posterSrc;
      motionToggle.setAttribute("aria-pressed", String(playing));
      motionToggle.textContent = playing
        ? "PAUSE COLOR MOTION"
        : "PLAY COLOR MOTION";
    };

    const calculateMatches = () => {
      if (!catalogData?.colors?.length) {
        state.matches = { a: [], b: [] };
        return;
      }
      const targets = schoolColors();
      ["a", "b"].forEach((side) => {
        state.matches[side] = catalogData.colors
          .map((color) => ({
            ...color,
            score: matchPercent(targets[side].hex, color.screenSampleHex),
          }))
          .sort((first, second) => {
            if (second.score !== first.score) return second.score - first.score;
            return first.name.localeCompare(second.name);
          });
      });
    };

    const renderSchoolTargets = () => {
      const targets = schoolColors();
      ["a", "b"].forEach((side) => {
        const card = section.querySelector(
          `[data-bag-school-target="${side}"]`,
        );
        if (!card) return;
        const target = targets[side];
        const chip = card.querySelector(".gameday-bag-product__school-chip");
        const name = card.querySelector(`[data-bag-school-color-name="${side}"]`);
        const reference = card.querySelector(
          `[data-bag-school-color-reference="${side}"]`,
        );
        if (chip) chip.style.backgroundColor = target.hex;
        if (name) name.textContent = target.name;
        if (reference) {
          reference.textContent = target.reference
            ? `${target.reference} · ${target.hex}`
            : `${target.hex} · SCREEN REFERENCE`;
        }
      });
    };

    const renderTopMatches = () => {
      ["a", "b"].forEach((side) => {
        const card = section.querySelector(`[data-bag-top-match="${side}"]`);
        if (!card) return;
        const match = state.matches[side][0];
        const chip = card.querySelector(".gameday-bag-product__match-chip");
        const name = card.querySelector("[data-bag-match-name]");
        const sku = card.querySelector("[data-bag-match-sku]");
        const percent = card.querySelector("[data-bag-match-percent]");
        if (!match) {
          if (name) name.textContent = "Catalog unavailable";
          if (sku) sku.textContent = "";
          if (percent) percent.textContent = "—";
          return;
        }
        if (chip) chip.style.backgroundColor = match.screenSampleHex;
        if (name) name.textContent = match.name;
        if (sku) sku.textContent = `FWD ${match.sku}`;
        if (percent) percent.textContent = `${match.score}%`;
      });
    };

    const appendText = (element, className, value) => {
      const child = document.createElement("span");
      child.className = className;
      child.textContent = value;
      element.appendChild(child);
      return child;
    };

    const renderCatalog = () => {
      if (!catalogGrid) return;
      catalogGrid.replaceChildren();
      const target = schoolColors()[state.catalogTarget];
      const matches = state.matches[state.catalogTarget];
      if (catalogSchool) {
        catalogSchool.textContent = `${
          state.school?.name || "OREGON"
        } / ${target.name}`;
      }
      if (!matches.length) {
        const unavailable = document.createElement("p");
        unavailable.textContent =
          "The current Ottertex color catalog is unavailable.";
        catalogGrid.appendChild(unavailable);
        return;
      }

      matches.forEach((match, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "gameday-bag-product__swatch";
        if (index === 0) button.classList.add("is-best");
        button.setAttribute(
          "aria-label",
          `Use ${match.name}, ${match.sku}, ${match.score}% screen match for Color ${state.catalogTarget.toUpperCase()}`,
        );

        const sample = appendText(
          button,
          "gameday-bag-product__swatch-sample",
          "",
        );
        sample.style.backgroundColor = match.screenSampleHex;
        const details = document.createElement("span");
        details.className = "gameday-bag-product__swatch-details";
        appendText(details, "gameday-bag-product__swatch-name", match.name);
        appendText(details, "gameday-bag-product__swatch-sku", match.sku);
        button.appendChild(details);
        appendText(
          button,
          "gameday-bag-product__swatch-score",
          `${match.score}% MATCH`,
        );
        if (index === 0) {
          appendText(button, "gameday-bag-product__swatch-best", "CLOSEST");
        }
        button.addEventListener("click", () => {
          const input = colorInputs[state.catalogTarget];
          if (input) input.value = match.screenSampleHex;
          updateConfiguration({ manual: true });
        });
        catalogGrid.appendChild(button);
      });
    };

    const renderSchool = () => {
      calculateMatches();
      renderSchoolTargets();
      renderTopMatches();
      renderCatalog();
      applyPhotoPreview();
    };

    const selectSchool = (schoolId) => {
      state.school =
        catalogData?.schools?.find((school) => school.id === schoolId) ||
        catalogData?.schools?.find((school) => school.id === "oregon") ||
        null;
      const targets = schoolColors();
      if (colorInputs.a) colorInputs.a.value = targets.a.hex;
      if (colorInputs.b) colorInputs.b.value = targets.b.hex;
      state.previewMode = "school";
      updateConfiguration();
      renderSchool();
    };

    const populateSchools = () => {
      if (!schoolSelect || !catalogData?.schools?.length) return;
      schoolSelect.replaceChildren();
      catalogData.schools.forEach((school) => {
        const option = document.createElement("option");
        option.value = school.id;
        option.textContent = school.name;
        schoolSelect.appendChild(option);
      });
      schoolSelect.value = "oregon";
    };

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setMotionState(false);
    }
    motionToggle?.addEventListener("click", () => {
      setMotionState(motionToggle.getAttribute("aria-pressed") !== "true");
    });
    schoolSelect?.addEventListener("change", () => {
      selectSchool(schoolSelect.value);
    });
    previewButtons.forEach((button) => {
      button.addEventListener("click", () => {
        state.previewMode = button.dataset.bagPreviewMode;
        applyPhotoPreview();
      });
    });
    catalogButtons.forEach((button) => {
      button.addEventListener("click", () => {
        state.catalogTarget = button.dataset.bagCatalogTarget;
        setButtonGroupState(
          catalogButtons,
          state.catalogTarget,
          "bagCatalogTarget",
        );
        renderCatalog();
      });
    });
    Object.values(colorInputs).forEach((input) => {
      input?.addEventListener("input", () => {
        updateConfiguration({ manual: true });
      });
    });
    section.querySelectorAll("[data-bag-palette-a]").forEach((button) => {
      button.addEventListener("click", () => {
        if (colorInputs.a) colorInputs.a.value = button.dataset.bagPaletteA;
        if (colorInputs.b) colorInputs.b.value = button.dataset.bagPaletteB;
        updateConfiguration({ manual: true });
      });
    });

    populateSchools();
    selectSchool("oregon");
  }

  function initializeAll(root = document) {
    root
      .querySelectorAll("[data-gameday-bag-product]")
      .forEach((section) => {
        initialize(section).catch((error) => {
          section.dataset.bagProductError =
            error instanceof Error ? error.message : String(error);
        });
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  } else {
    initializeAll();
  }
  document.addEventListener("shopify:section:load", (event) => {
    initializeAll(event.target);
  });
})();
